import { useMemo, useState, useEffect } from 'react'
import ReactMarkdown, { type Options as ReactMarkdownOptions } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import 'katex/dist/katex.min.css'
import { SafeMarkdown } from './SafeMarkdown'
import {
  mergeMarkdownComponents,
  remarkPlugins,
  rehypePlugins,
} from '@/config/markdownConfig'
import { Button } from '@/components/ui/button'
import { Pencil, Save, X } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from '@/i18n'

// react-markdown's plugin props are typed as `PluggableList | null | undefined`.
// Derive the non-null list type from its public Options surface so we don't
// import from the transitive `unified` package directly.
type PluggableList = NonNullable<ReactMarkdownOptions['remarkPlugins']>

interface MarkdownViewerProps {
  content: string | null
  currentPage: number
  onPageChange: (page: number) => void
  onTotalPagesChange: (total: number) => void
  isPageContent?: boolean // If true, content is already for a single page
  docId?: string // Document ID for saving edits
  onContentUpdate?: (newContent: string, pageNumber: number) => void // Callback when content is updated
}

// Parse markdown content with XML comment page delimiters
const parseMarkdownPages = (content: string): string[] => {
  // Look for XML comments that mark page boundaries
  // Patterns to match: <!-- Page X -->, <!-- PAGE X -->, etc.
  const pageDelimiterRegex = /<!--\s*[Pp]age\s*(\d+)\s*-->/g

  const pages: string[] = []
  const matches: Array<{ index: number; pageNum: number; fullMatch: string }> =
    []

  // Find all page delimiters
  let match
  while ((match = pageDelimiterRegex.exec(content)) !== null) {
    matches.push({
      index: match.index,
      pageNum: parseInt(match[1], 10),
      fullMatch: match[0],
    })
  }

  // If no page delimiters found, treat entire content as one page
  if (matches.length === 0) {
    return [content]
  }

  // Sort matches by index to ensure proper order
  matches.sort((a, b) => a.index - b.index)

  // Extract pages based on delimiters
  matches.forEach((match, index) => {
    // Content before the first delimiter (if any)
    if (index === 0 && match.index > 0) {
      pages.push(content.substring(0, match.index).trim())
    }

    // Content between delimiters
    const startIndex = match.index + match.fullMatch.length
    const endIndex =
      index < matches.length - 1 ? matches[index + 1].index : content.length
    const pageContent = content.substring(startIndex, endIndex).trim()

    // Ensure we're adding pages in the right order
    while (pages.length < match.pageNum - 1) {
      pages.push('') // Add empty pages if page numbers skip
    }

    pages[match.pageNum - 1] = pageContent
  })

  return pages.filter((page) => page.length > 0) // Remove empty pages
}

export const MarkdownViewer = ({
  content,
  currentPage,
  onPageChange,
  onTotalPagesChange,
  isPageContent = false,
  docId,
  onContentUpdate,
}: MarkdownViewerProps) => {
  const { t } = useTranslation()
  const [isEditing, setIsEditing] = useState(false)
  const [editedContent, setEditedContent] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  // Reset edit state when page changes
  useEffect(() => {
    if (isEditing) {
      setIsEditing(false)
      setEditedContent('')
    }
  }, [currentPage])

  const { pages, isMarkdown, currentPageContent } = useMemo(() => {
    if (!content)
      return { pages: [], isMarkdown: false, currentPageContent: '' }

    // Check if content has markdown patterns
    // For page content mode, always treat as markdown to ensure consistent styling
    const hasMarkdown = isPageContent
      ? true
      : content.includes('#') ||
        content.includes('|') ||
        content.includes('**') ||
        content.includes('```') ||
        content.includes('[') ||
        content.includes('<!--') ||
        content.includes('\n-') || // Lists
        content.includes('\n*') || // Lists
        content.includes('\n·') || // Bullet points
        content.includes('<table') || // HTML tables
        content.includes('\n1.') // Numbered lists

    if (isPageContent) {
      // Content is already for a single page
      onTotalPagesChange(1)
      return {
        pages: [content],
        isMarkdown: hasMarkdown,
        currentPageContent: content,
      }
    } else {
      // Parse pages from full document
      const parsedPages = parseMarkdownPages(content)
      onTotalPagesChange(parsedPages.length)
      return {
        pages: parsedPages,
        isMarkdown: hasMarkdown,
        currentPageContent: parsedPages[currentPage - 1] || '',
      }
    }
  }, [content, onTotalPagesChange, isPageContent, currentPage])

  const handleStartEdit = () => {
    setEditedContent(currentPageContent)
    setIsEditing(true)
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
    setEditedContent('')
  }

  const handleSaveEdit = async () => {
    if (!docId || !onContentUpdate) {
      toast.error(t('common:document_viewer.toasts.cannot_save_no_doc_id'))
      return
    }

    setIsSaving(true)
    try {
      // Import apiService dynamically to avoid circular dependencies
      const { apiService } = await import('@/service/api')
      
      // Page is 0-indexed in backend, currentPage is 1-indexed
      await apiService.updateDocumentPageText(docId, currentPage - 1, editedContent)
      
      // Update parent component with the page that was edited
      onContentUpdate(editedContent, currentPage)
      
      toast.success(t('common:document_viewer.toasts.page_text_updated'))
      setIsEditing(false)
      setEditedContent('')
    } catch (error) {
      console.error('Failed to save page text:', error)
      toast.error(t('common:document_viewer.toasts.failed_to_save'))
    } finally {
      setIsSaving(false)
    }
  }

  if (!content) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-gray-500 dark:text-dark-text-muted">No text content available</div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar - only show if not page content */}
      {!isPageContent && (
        <div className="flex items-center justify-between p-2 border-b bg-gray-50 dark:bg-dark-muted">
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage <= 1}
              className="px-3 py-1 text-sm bg-white dark:bg-dark-card border rounded hover:bg-gray-100 dark:hover:bg-dark-accent disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm">
              Page {currentPage} of {pages.length || 1}
            </span>
            <button
              onClick={() =>
                onPageChange(Math.min(pages.length, currentPage + 1))
              }
              disabled={currentPage >= pages.length}
              className="px-3 py-1 text-sm bg-white dark:bg-dark-card border rounded hover:bg-gray-100 dark:hover:bg-dark-accent disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Edit Controls - Only show when docId is provided (HITL mode) */}
      {docId && isPageContent && (
        <div className={`flex items-center justify-between p-3 border-b ${isEditing ? 'bg-orange-50 dark:bg-orange-900/20' : 'bg-blue-50 dark:bg-blue-900/20'}`}>
          <span className={`text-sm font-medium pl-16 ${isEditing ? 'text-orange-700 dark:text-orange-400' : 'text-blue-700 dark:text-blue-400'}`}>
            {isEditing ? '✏️ Editing mode - Make your corrections below' : 'Click "Edit Text" to correct parsing errors'}
          </span>
          <div className="flex items-center gap-2">
            {!isEditing ? (
              <Button
                size="sm"
                onClick={handleStartEdit}
                className="flex items-center gap-2 bg-blue-600 text-white hover:bg-blue-700"
              >
                <Pencil className="w-4 h-4" />
                Edit Text
              </Button>
            ) : (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                  className="flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSaveEdit}
                  disabled={isSaving}
                  className="flex items-center gap-2 bg-green-600 text-white hover:bg-green-700"
                >
                  <Save className="w-4 h-4" />
                  {isSaving ? 'Saving...' : 'Publish Changes'}
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Content Display */}
      <div className="flex-1 overflow-auto">
        {isEditing ? (
          // Edit mode - show textarea
          <div className="p-4 h-full">
            <textarea
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              className="w-full h-full p-3 font-mono text-sm border rounded resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Edit the extracted text here..."
            />
          </div>
        ) : isMarkdown && currentPageContent && currentPageContent.length <= 50_000 ? (
          <SafeMarkdown
            fallback={
              <pre className="h-full overflow-auto font-mono whitespace-pre-wrap break-words text-sm p-4">
                {currentPageContent}
              </pre>
            }
          >
            <div className="p-4 h-full overflow-auto">
              <div className="font-mono text-sm whitespace-pre-wrap break-words leading-normal font-normal">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm, ...remarkPlugins] as PluggableList}
                  rehypePlugins={rehypePlugins as PluggableList}
                  components={mergeMarkdownComponents({
                  // Keep the specific table styling for PDF viewer
                  // Note: node prop must be destructured (react-markdown v9+) to prevent [object Object] rendering
                  th: ({ children, node: _node, ...props }) => (
                    <th
                      className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left text-sm font-semibold text-gray-900 dark:text-dark-text"
                      {...props}
                    >
                      {children}
                    </th>
                  ),
                  td: ({ children, node: _node, ...props }) => (
                    <td
                      className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-600 dark:text-gray-400"
                      {...props}
                    >
                      {children}
                    </td>
                  ),
                  tr: ({ children, node: _node, ...props }) => (
                    <tr className="hover:bg-gray-50 dark:hover:bg-dark-muted" {...props}>
                      {children}
                    </tr>
                  ),
                  // Keep font-mono for PDF viewer context with consistent text-sm sizing
                  p: ({ children }) => <p className="my-1 text-sm leading-normal">{children}</p>,
                  code: ({ className, children, node: _node, ...props }) => {
                    const match = /language-(\w+)/.exec(className || '')
                    const isInline = !match
                    return isInline ? (
                      <code
                        className="bg-gray-100 dark:bg-dark-accent px-1 rounded text-sm font-mono"
                        {...props}
                      >
                        {children}
                      </code>
                    ) : (
                      <pre className="bg-gray-100 dark:bg-dark-accent p-2 rounded my-2 overflow-x-auto">
                        <code className="text-sm font-mono" {...props}>
                          {children}
                        </code>
                      </pre>
                    )
                  },
                  h1: ({ children }) => (
                    <h1 className="text-sm font-normal mt-4 mb-2">
                      {children}
                    </h1>
                  ),
                  h2: ({ children }) => (
                    <h2 className="text-sm font-normal mt-3 mb-2">
                      {children}
                    </h2>
                  ),
                  h3: ({ children }) => (
                    <h3 className="text-sm font-normal mt-2 mb-1">
                      {children}
                    </h3>
                  ),
                  h4: ({ children }) => (
                    <h4 className="text-sm font-normal mt-2 mb-1">
                      {children}
                    </h4>
                  ),
                  h5: ({ children }) => (
                    <h5 className="text-sm font-normal mt-2 mb-1">
                      {children}
                    </h5>
                  ),
                  h6: ({ children }) => (
                    <h6 className="text-sm font-normal mt-2 mb-1">
                      {children}
                    </h6>
                  ),
                  // Blockquotes
                  blockquote: ({ children }) => (
                    <blockquote className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 my-2 italic text-sm">
                      {children}
                    </blockquote>
                  ),
                  // Lists with consistent sizing
                  ul: ({ children }) => (
                    <ul className="list-disc list-outside mb-2 space-y-1 ml-6 text-sm">
                      {children}
                    </ul>
                  ),
                  ol: ({ children }) => (
                    <ol className="list-decimal list-outside mb-2 space-y-1 ml-6 text-sm">
                      {children}
                    </ol>
                  ),
                  li: ({ children }) => (
                    <li className="text-sm">
                      {children}
                    </li>
                  ),
                  // Strong and emphasis - remove bold to maintain consistent weight
                  strong: ({ children }) => (
                    <span className="font-normal text-sm">
                      {children}
                    </span>
                  ),
                  em: ({ children }) => (
                    <em className="italic text-sm">
                      {children}
                    </em>
                  ),
                })}
                >
                  {currentPageContent}
                </ReactMarkdown>
              </div>
            </div>
          </SafeMarkdown>
        ) : currentPageContent ? (
          // Plain text display - match the original styling
          <pre className="h-full overflow-auto font-mono whitespace-pre-wrap break-words text-sm p-4">
            {currentPageContent}
          </pre>
        ) : (
          // No content
          <div className="h-full flex items-center justify-center">
            <div className="text-gray-500 dark:text-dark-text-muted">No content available</div>
          </div>
        )}
      </div>
    </div>
  )
}
